/**
 * Gifts with purchase in the bag (handover/sprint-2026-10-gwp/001).
 *
 * At each spend tier (Theme settings > Gifts with purchase) the bag gets that
 * tier's gift, and the gift comes out again when the bag drops below. A
 * Shopify discount makes the gift free at checkout (one Buy X get Y per tier);
 * this file only decides which gift lines belong in the bag and draws the
 * progress rail and the notices. It never prices anything.
 *
 * Facts come from snippets/gwp-state-json.liquid, which the drawer and the
 * cart page re-render inside a data-merge region on every cart change: spend
 * (line prices after automatic discounts, without protection, gift cards,
 * gift-tagged products or gift lines), the collection offer shown as the
 * first stop, the live tiers for this shopper with their gift products, and
 * every bag line that holds one of them.
 *
 * Rules (001 §3):
 *   - a tier is met when spend >= its amount, and "present" when any line of
 *     its gift products is in the bag, tagged or bought;
 *   - met, not present, not declined: add the remembered colour, else the
 *     first in stock, as one unit with line property _gwp = tier, posted with
 *     ?darmha_src=gift so it is not reported as a product add;
 *   - only tagged lines are ever removed: when their tier is not met, not
 *     live or declined, or (gwp_require_discount) when no unit of the tier's
 *     products has been made free. The tier is then "unconfirmed" until the
 *     bag changes, so nothing is put back in a loop;
 *   - the customer's choices are cart attributes: _gwp_choice
 *     ("1:<productId>,2:<productId>") and _gwp_declined ("2"), cleared when
 *     the bag empties.
 *
 * The rail and notices live in hosts outside every data-merge region
 * ([data-gwp-rail], [data-gwp-notices]), so a merge never wipes them and the
 * rail animates between states. Gift lines themselves are Liquid
 * (snippets/gwp-line.liquid).
 *
 * Everything above the "Runtime" line is pure and exposed on window.theme.gwp
 * so tests/gwp.test.js can load this file in a vm sandbox with no DOM.
 */

(function () {
  'use strict';

  const root = typeof window !== 'undefined' ? window : globalThis;
  const theme = (root.theme = root.theme || {});

  const G = (theme.gwp = theme.gwp || {});

  G.PROPERTY = '_gwp';
  G.ATTR_CHOICE = '_gwp_choice';
  G.ATTR_DECLINED = '_gwp_declined';
  G.ADD_URL = '/cart/add.js?darmha_src=gift';
  G.PROTECTION_VENDOR = 're:do';
  G.PROTECTION_TITLES = ['free returns', 'package protection', 'shipping protection'];

  // ---------------------------------------------------------------------------
  // Pure — no DOM below this line until "Runtime".
  // ---------------------------------------------------------------------------

  /** "1:123,2:456" → {1: 123, 2: 456}. Anything malformed is dropped. */
  G.parseChoice = function parseChoice(value) {
    const out = {};
    String(value || '').split(',').forEach((pair) => {
      const bits = pair.split(':');
      const tier = parseInt(bits[0], 10);
      const product = parseInt(bits[1], 10);
      if (tier > 0 && product > 0) out[tier] = product;
    });
    return out;
  };

  G.serializeChoice = function serializeChoice(map) {
    return Object.keys(map || {})
      .map(Number)
      .filter((n) => n > 0 && Number(map[n]) > 0)
      .sort((a, b) => a - b)
      .map((n) => n + ':' + Number(map[n]))
      .join(',');
  };

  /** "2,1" → [1, 2]. */
  G.parseDeclined = function parseDeclined(value) {
    const out = [];
    String(value || '').split(',').forEach((bit) => {
      const n = parseInt(bit, 10);
      if (n > 0 && out.indexOf(n) === -1) out.push(n);
    });
    return out.sort((a, b) => a - b);
  };

  G.serializeDeclined = function serializeDeclined(list) {
    return G.parseDeclined((list || []).join(',')).join(',');
  };

  /** A tier or offer is live until its end (Unix seconds; 0 = no end). */
  G.isLive = function isLive(entry, nowMs) {
    const end = Number(entry && entry.end) || 0;
    return !(end > 0) || end * 1000 > nowMs;
  };

  /**
   * The tiers this shopper can earn right now: live, with an amount, and
   * offering at least one one-size product. A collection of sized products
   * is not offered yet (001 §3.9): it is skipped, never added in a guessed
   * size.
   */
  G.liveTiers = function liveTiers(state, nowMs) {
    if (!state || state.wholesale) return [];
    return (state.tiers || []).filter((t) => {
      if (!G.isLive(t, nowMs) || !(Number(t.amount) > 0)) return false;
      const options = t.options || [];
      return options.length > 0 && options.some((o) => !o.sized);
    });
  };

  G.liveOffer = function liveOffer(state, nowMs) {
    const offer = state && !state.wholesale ? state.offer : null;
    if (!offer || !G.isLive(offer, nowMs) || !(Number(offer.min) > 0)) return null;
    return offer;
  };

  /**
   * Shopify can list one line as two entries with the same key when a
   * discount covers only some of its units (the bag templates note it for
   * BOGOFs). One key is one line: quantities and discounts are added up, so
   * the duplicate rule never removes a whole line for a split it caused.
   */
  G.mergeLines = function mergeLines(lines) {
    const byKey = {};
    const out = [];
    (lines || []).forEach((l) => {
      const seen = byKey[l.key];
      if (seen) {
        seen.qty = Number(seen.qty) + Number(l.qty);
        seen.discount = Number(seen.discount) + Number(l.discount);
      } else {
        byKey[l.key] = Object.assign({}, l);
        out.push(byKey[l.key]);
      }
    });
    return out;
  };

  /**
   * Everything the rail, the notices and the planner need about one tier.
   * `unconfirmed` is the planner's memory: tier → the cart signature at which
   * the discount did not make its gift free.
   */
  G.tierFacts = function tierFacts(state, tier, choice, declined, unconfirmed) {
    const spend = Number(state.spend) || 0;
    const amount = Number(tier.amount) || 0;
    const lines = G.mergeLines(tier.lines);
    const options = tier.options || [];
    const tagged = lines.filter((l) => l.tagged);
    const available = options.filter((o) => o.available && !o.sized);
    const chosen = (choice && choice[tier.n]) || null;
    const pick = available.find((o) => o.p === chosen) || available[0] || null;
    const met = spend >= amount;
    const present = lines.length > 0;
    const free = lines.some((l) => Number(l.unit) > 0 && Number(l.discount) >= Number(l.unit));
    const isDeclined = (declined || []).indexOf(tier.n) !== -1;
    const gone = available.length === 0 && !present;
    const isUnconfirmed = !!(unconfirmed && state.signature && unconfirmed[tier.n] === state.signature);
    const inBagOption = tagged.length ? options.find((o) => o.p === tagged[0].p) : null;
    return {
      n: tier.n,
      name: tier.name || '',
      amount,
      met,
      present,
      free,
      tagged,
      options,
      available,
      chosen,
      pick,
      kept: options.find((o) => o.p === chosen) || pick,
      inBagOption,
      declined: isDeclined,
      gone,
      unconfirmed: isUnconfirmed,
      held: !met && !!chosen && !isDeclined && !gone
    };
  };

  /** Plain text: "[gift] unlocked" + {gift: 'Tote'} → "Tote unlocked". */
  G.text = function text(template, values) {
    return String(template || '').replace(/\[([a-z_]+)\]/g, (whole, key) => (
      Object.prototype.hasOwnProperty.call(values || {}, key) ? String(values[key]) : whole
    ));
  };

  /**
   * Template → parts for safe rendering: placeholder values are bold, the
   * rest is text. Unknown placeholders stay as typed.
   */
  G.fill = function fill(template, values) {
    const parts = [];
    String(template || '').split(/(\[[a-z_]+\])/).forEach((chunk) => {
      if (!chunk) return;
      const m = /^\[([a-z_]+)\]$/.exec(chunk);
      if (m && Object.prototype.hasOwnProperty.call(values || {}, m[1])) {
        parts.push({ b: true, v: String(values[m[1]]) });
      } else {
        parts.push({ b: false, v: chunk });
      }
    });
    return parts;
  };

  G.partsText = function partsText(parts) {
    return (parts || []).map((p) => p.v).join('');
  };

  const SEP = { b: false, v: ' · ' };

  /**
   * The bag's gift picture for one state: the stops in order (the offer,
   * then gifts and free shipping by amount), each with its fill and state;
   * the one-line message; the notices. `fmt.money` / `fmt.money0` format
   * cents (with / without trailing zeros).
   */
  G.model = function model(state, opts) {
    const o = opts || {};
    const nowMs = o.now || Date.now();
    const tiers = G.liveTiers(state, nowMs);
    // Nothing to show for an empty bag (the cart page keeps its own empty
    // state; the drawer hides its content).
    if (!state || !tiers.length || !(Number(state.realCount) > 0)) return { active: false };

    const fmt = o.fmt || { money: (c) => String(c), money0: (c) => String(c) };
    const copy = state.copy || {};
    const labels = state.labels || {};
    const choice = G.parseChoice(state.choice);
    const declined = G.parseDeclined(state.declined);
    const spend = Number(state.spend) || 0;
    const offer = G.liveOffer(state, nowMs);
    const offerMet = !offer || Number(offer.count) >= Number(offer.min);
    const facts = tiers.map((t) => G.tierFacts(state, t, choice, declined, o.unconfirmed));

    // Spend rungs by amount; free shipping before a gift at the same amount.
    const rungs = facts.map((f) => ({ kind: f.gone ? 'gone' : 'gift', at: f.amount, tier: f }));
    const ship = Number(state.shipping) || 0;
    if (ship > 0) rungs.push({ kind: 'ship', at: ship, tier: null });
    rungs.sort((a, b) => (a.at - b.at) || ((a.kind === 'ship' ? 0 : 1) - (b.kind === 'ship' ? 0 : 1)));

    // Gift progress waits for the offer (001 §3.3): unlocking it lowers every
    // price, so a partly filled gift segment would go backwards. A rung whose
    // amount is fully met still shows met.
    let prev = 0;
    rungs.forEach((r) => {
      r.met = spend >= r.at;
      r.fill = r.met ? 1 : ((offerMet && spend > prev && r.at > prev) ? (spend - prev) / (r.at - prev) : 0);
      prev = r.at;
    });

    const stops = [];
    if (offer) {
      stops.push({
        kind: 'offer',
        at: 0,
        met: offerMet,
        fill: Math.max(0, Math.min(Number(offer.count) / Number(offer.min), 1)),
        tier: null
      });
    }
    rungs.forEach((r) => stops.push(r));

    stops.forEach((s) => {
      if (s.kind === 'offer') {
        s.name = offer.label;
        s.req = G.text(labels.items, { min: offer.min });
      } else if (s.kind === 'ship') {
        s.name = labels.freeShipping || 'Free shipping';
        s.req = fmt.money0(s.at);
      } else {
        s.name = s.tier.name;
        s.req = s.kind === 'gone' ? (labels.allClaimed || 'All claimed') : fmt.money0(s.at);
      }
      // A gift the discount has not confirmed is never shown as unlocked.
      const ticked = s.met && s.kind !== 'gone' && !(s.tier && s.tier.unconfirmed);
      s.state = s.kind === 'gone' ? 'gone' : (ticked ? 'met' : 'todo');
    });

    const gone = stops.filter((s) => s.kind === 'gone').length;
    const unlocked = stops.filter((s) => s.state === 'met').length;
    const m = { active: true, spend, offer, offerMet, facts, rungs, stops, unlocked, total: stops.length - gone, gone };
    m.message = G.message(m, copy, labels, fmt);
    m.notices = G.notices(m, copy, labels, fmt);
    m.keys = G.unlockedKeys(m);
    return m;
  };

  /** The one-line message: always the next thing to do (001 §3.6). */
  G.message = function message(m, copy, labels, fmt) {
    const reward = (r) => (r.kind === 'ship'
      ? (labels.freeShipping || 'Free shipping')
      : G.text(labels.freeReward || 'Free [gift]', { gift: r.tier.name }));
    const lead = (rewardText) => (rewardText && copy.unlocked
      ? G.fill(copy.unlocked, { reward: rewardText }).concat([SEP])
      : []);

    if (m.offer && !m.offerMet) {
      const remaining = Math.max(1, Number(m.offer.min) - Number(m.offer.count));
      const inBag = m.facts.filter((f) => f.met && f.present);
      const last = inBag.length ? inBag[inBag.length - 1] : null;
      return lead(last ? G.text(labels.freeReward || 'Free [gift]', { gift: last.name }) : null)
        .concat(G.fill(remaining === 1 ? copy.offerOne : copy.offerMany, { remaining, offer: m.offer.label }));
    }

    const skip = (r) => r.kind === 'gone' || !!(r.tier && (r.tier.declined || r.tier.unconfirmed));
    const next = m.rungs.find((r) => !r.met && !skip(r));
    if (!next) return G.fill(copy.done, {});

    const amount = fmt.money(next.at - m.spend);
    if (next.tier && next.tier.held) return G.fill(copy.hold, { amount, gift: next.tier.name });

    let lastMet = null;
    m.rungs.forEach((r) => { if (r.met && !skip(r)) lastMet = r; });
    const rewardText = lastMet ? reward(lastMet) : (m.offer ? m.offer.label : null);
    return lead(rewardText).concat(next.kind === 'ship'
      ? G.fill(copy.shipping, { amount })
      : G.fill(copy.gift, { amount, gift: next.tier.name }));
  };

  /** On hold cards, "add it back" rows and "all claimed" rows (001 §3.7). */
  G.notices = function notices(m, copy, labels, fmt) {
    const out = [];
    m.facts.forEach((f) => {
      if (f.held) {
        const kept = f.kept;
        out.push({
          type: 'hold',
          n: f.n,
          title: G.fill(copy.holdTitle, { gift: f.name }),
          body: G.fill(copy.holdBody, { amount: fmt.money(f.amount - m.spend), colour: kept ? kept.colour : '' }),
          image: kept ? kept.image : null
        });
      } else if (f.met && f.declined && !f.gone) {
        out.push({
          type: 'declined',
          n: f.n,
          text: G.fill(copy.declined, { gift: f.name }),
          action: labels.addBack || 'Add it back',
          actionLabel: G.text(labels.addBackLabel || 'Add your free [gift] back', { gift: f.name })
        });
      } else if (f.met && f.gone) {
        out.push({ type: 'claimed', n: f.n, text: G.fill(copy.claimed, { gift: f.name }) });
      }
    });
    return out;
  };

  /** What is unlocked right now, for the one-off banners (001 §3.10). */
  G.unlockedKeys = function unlockedKeys(m) {
    const keys = [];
    if (m.offer && m.offerMet) keys.push('offer');
    m.facts.forEach((f) => { if (f.met && f.tagged.length && !f.unconfirmed) keys.push('g' + f.n); });
    return keys;
  };

  /**
   * The banner for keys that were not unlocked last time (a gift beats the
   * offer, the newest gift first), or null.
   */
  G.banner = function banner(m, previousKeys, copy, labels) {
    if (!m || !m.active || !previousKeys) return null;
    const fresh = m.keys.filter((k) => previousKeys.indexOf(k) === -1);
    const gifts = fresh.filter((k) => k.charAt(0) === 'g');
    if (gifts.length) {
      const n = Number(gifts[gifts.length - 1].slice(1));
      const f = m.facts.find((x) => x.n === n);
      if (f) {
        const tail = f.available.length > 1
          ? (labels.chooseBelow || '')
          : G.text(labels.itsIn || '', { colour: f.inBagOption ? f.inBagOption.colour : '' });
        const parts = G.fill(copy.bannerGift, { gift: f.name });
        if (tail) parts.push({ b: false, v: ' ' + tail });
        return { key: 'g' + n, parts };
      }
    }
    if (fresh.indexOf('offer') !== -1 && copy.bannerOffer) {
      return { key: 'offer', parts: G.fill(copy.bannerOffer, { offer: m.offer.label }) };
    }
    return null;
  };

  function hasWork(p) {
    return Object.keys(p.updates).length > 0 || Object.keys(p.attributes).length > 0 || p.adds.length > 0;
  }

  /** One gift unit for /cart/add.js, tagged with its tier. */
  G.addItem = function addItem(tier, option) {
    return { id: option.v, quantity: 1, properties: { [G.PROPERTY]: String(tier.n) } };
  };

  /**
   * The adds still needed against the live cart (/cart.js), just before
   * posting them. A plan is made from the last render, and two renders can
   * overlap (protection's refresh and ours), so a plan can be one add behind:
   * a gift whose tier already has a tagged line, or a line of the same
   * product, is not added again.
   */
  G.pendingAdds = function pendingAdds(adds, cart, state) {
    const items = (cart && cart.items) || [];
    return (adds || []).filter((add) => {
      const n = add.properties[G.PROPERTY];
      const tier = ((state && state.tiers) || []).find((t) => String(t.n) === n);
      const products = ((tier && tier.options) || []).map((o) => o.p);
      return !items.some((item) => (
        (item.properties && item.properties[G.PROPERTY] === n)
        || products.indexOf(Number(item.product_id)) !== -1
      ));
    });
  };

  /**
   * Bring the bag in line with the rules. Returns
   * { updates, attributes, adds, unconfirm } for /cart/update.js and
   * /cart/add.js, or null when nothing needs to change. Idempotent: the
   * cart it produces plans nothing.
   */
  G.plan = function plan(state, opts) {
    if (!state) return null;
    const o = opts || {};
    const nowMs = o.now || Date.now();
    const p = { updates: {}, attributes: {}, adds: [], unconfirm: {} };
    const remove = (line) => { p.updates[line.key] = 0; };
    const live = G.liveTiers(state, nowMs);
    const liveNs = live.map((t) => t.n);

    // Tagged lines whose tier is not live here: not in this render, or
    // ended since the page was rendered.
    (state.orphans || []).forEach(remove);
    (state.tiers || []).forEach((t) => {
      if (liveNs.indexOf(t.n) === -1) (t.lines || []).filter((l) => l.tagged).forEach(remove);
    });

    // An empty bag keeps no gift and forgets every choice.
    if (!(Number(state.realCount) > 0)) {
      live.forEach((t) => (t.lines || []).filter((l) => l.tagged).forEach(remove));
      if (state.choice) p.attributes[G.ATTR_CHOICE] = '';
      if (state.declined) p.attributes[G.ATTR_DECLINED] = '';
      return hasWork(p) ? p : null;
    }

    const choice = G.parseChoice(state.choice);
    const declined = G.parseDeclined(state.declined);
    let choiceChanged = false;

    live.forEach((t) => {
      const f = G.tierFacts(state, t, choice, declined, o.unconfirmed);
      if (!f.met || f.declined) {
        f.tagged.forEach(remove);
        return;
      }
      // Never charge for a "free" gift (001 §1): the discount must have made
      // a unit of the tier's products free, or the tagged line goes.
      if (state.requireDiscount && f.present && !f.free) {
        f.tagged.forEach(remove);
        p.unconfirm[t.n] = state.signature;
        return;
      }
      f.tagged.slice(1).forEach(remove);
      if (f.tagged.length && Number(f.tagged[0].qty) > 1) p.updates[f.tagged[0].key] = 1;
      if (f.present || f.unconfirmed || !f.pick) return;
      p.adds.push(G.addItem(t, f.pick));
      if (choice[t.n] !== f.pick.p) {
        choice[t.n] = f.pick.p;
        choiceChanged = true;
      }
    });

    if (choiceChanged) p.attributes[G.ATTR_CHOICE] = G.serializeChoice(choice);
    if (hasWork(p) || Object.keys(p.unconfirm).length) return p;
    return null;
  };

  /** The customer picked another colour for tier n. */
  G.planSwap = function planSwap(state, n, productId, nowMs) {
    const tier = G.liveTiers(state, nowMs || Date.now()).find((t) => t.n === Number(n));
    if (!tier) return null;
    const option = (tier.options || []).find((o) => o.p === Number(productId) && o.available && !o.sized);
    if (!option) return null;
    const tagged = G.mergeLines(tier.lines).filter((l) => l.tagged);
    if (tagged.length === 1 && tagged[0].p === option.p) return null;
    const choice = G.parseChoice(state.choice);
    choice[tier.n] = option.p;
    const p = { updates: {}, attributes: { [G.ATTR_CHOICE]: G.serializeChoice(choice) }, adds: [], unconfirm: {} };
    tagged.forEach((l) => { p.updates[l.key] = 0; });
    p.adds.push(G.addItem(tier, option));
    return p;
  };

  /** The customer removed the gift of tier n: take it out and remember. */
  G.planDecline = function planDecline(state, n) {
    const tier = (state.tiers || []).find((t) => t.n === Number(n));
    const declined = G.parseDeclined(state.declined);
    if (declined.indexOf(Number(n)) === -1) declined.push(Number(n));
    const p = { updates: {}, attributes: { [G.ATTR_DECLINED]: G.serializeDeclined(declined) }, adds: [], unconfirm: {} };
    ((tier && tier.lines) || []).filter((l) => l.tagged).forEach((l) => { p.updates[l.key] = 0; });
    return p;
  };

  /** "Add it back": forget the decline and add the gift in the same step. */
  G.planAddBack = function planAddBack(state, n, opts) {
    const declined = G.parseDeclined(state.declined).filter((x) => x !== Number(n));
    const next = Object.assign({}, state, { declined: G.serializeDeclined(declined) });
    const p = G.plan(next, opts) || { updates: {}, attributes: {}, adds: [], unconfirm: {} };
    p.attributes[G.ATTR_DECLINED] = G.serializeDeclined(declined);
    return p;
  };

  /**
   * The signature Liquid writes for the cart, rebuilt from /cart.js so a
   * stale render can be spotted on page load (001 §3.11): currency, then
   * key:quantity:final_line_price for every line that is neither protection
   * nor a gift line.
   */
  G.isProtection = function isProtection(item) {
    if (!item) return false;
    if (String(item.vendor || '').toLowerCase() === G.PROTECTION_VENDOR) return true;
    const title = String(item.product_title || item.title || '').toLowerCase();
    return !!title && G.PROTECTION_TITLES.some((t) => title.indexOf(t) !== -1);
  };

  G.cartSignature = function cartSignature(cart) {
    let sig = String((cart && cart.currency) || '') + '|';
    ((cart && cart.items) || []).forEach((item) => {
      if (G.isProtection(item)) return;
      if (item.properties && item.properties[G.PROPERTY]) return;
      sig += item.key + ':' + item.quantity + ':' + item.final_line_price + ',';
    });
    return sig;
  };

  // ---------------------------------------------------------------------------
  // Runtime — everything below needs a DOM.
  // ---------------------------------------------------------------------------

  if (typeof document === 'undefined') return;

  const MAX_ATTEMPTS = 3;
  const UNCONFIRMED_KEY = 'gwpUnconfirmed';
  const KEYS_KEY = 'gwpUnlocked';
  const session = (() => { try { return root.sessionStorage; } catch (e) { return null; } })();

  function readJson(key) {
    try { return JSON.parse(session.getItem(key)); } catch (e) { return null; }
  }
  function writeJson(key, value) {
    try { session.setItem(key, JSON.stringify(value)); } catch (e) { /* ignore */ }
  }

  function money(cents) {
    return typeof theme.formatMoney === 'function' ? theme.formatMoney(cents) : (cents / 100).toFixed(2);
  }
  function money0(cents) {
    if (Number(cents) % 100 !== 0 || typeof theme.formatMoney !== 'function') return money(cents);
    return theme.formatMoney(cents, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }
  const FMT = { money, money0 };

  function readState() {
    const el = document.querySelector('script[data-gwp-state]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function refreshCart() {
    document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then((r) => {
      if (!r.ok) throw new Error(url + ' ' + r.status);
      return r.json();
    });
  }

  function track(name, params) {
    try { if (typeof root.gtag === 'function') root.gtag('event', name, params || {}); } catch (e) { /* ignore */ }
  }

  /**
   * Removals and attributes first, then the adds the live cart still needs
   * (G.pendingAdds); one refresh at the end. Resolves to the tiers added.
   */
  function execute(p, state) {
    let added = [];
    let chain = Promise.resolve();
    if (Object.keys(p.updates).length || Object.keys(p.attributes).length) {
      chain = chain.then(() => post('/cart/update.js', { updates: p.updates, attributes: p.attributes }));
    }
    if (p.adds.length) {
      chain = chain
        .then(() => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => (r.ok ? r.json() : null)))
        .then((cart) => {
          const adds = cart ? G.pendingAdds(p.adds, cart, state) : p.adds;
          if (!adds.length) return null;
          added = adds.map((a) => a.properties[G.PROPERTY]);
          // A sold-out gift fails here (422); the next render shows the stock.
          return post(G.ADD_URL, { items: adds }).catch((e) => {
            added = [];
            // eslint-disable-next-line no-console
            console.warn('[Gifts] add failed', e);
          });
        });
    }
    return chain.then(() => { refreshCart(); return added; });
  }

  // --- Rendering -------------------------------------------------------------

  const ICONS = {
    offer: '<svg class="gwp-rail__icon gwp-rail__icon--offer" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 5 5 19"></path><circle cx="7" cy="7" r="2.5"></circle><circle cx="17" cy="17" r="2.5"></circle></svg>',
    gift: '<svg class="gwp-rail__icon gwp-rail__icon--gift" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 11h16v10H4z"></path><path d="M3 7h18v4H3z"></path><path d="M12 7v14"></path><path d="M12 7c-1.5-3-5-3.5-5-1.5S10 7 12 7c2 0 5-.5 5-1.5S13.5 4 12 7z"></path></svg>',
    ship: '<svg class="gwp-rail__icon gwp-rail__icon--ship" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7h11v9H3z"></path><path d="M14 10h4l3 3v3h-7z"></path><circle cx="7" cy="17.5" r="1.8"></circle><circle cx="17" cy="17.5" r="1.8"></circle></svg>',
    gone: '<svg class="gwp-rail__icon gwp-rail__icon--gone" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" aria-hidden="true"><path d="M6 12h12"></path></svg>',
    tick: '<svg class="gwp-rail__tick" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5"></path></svg>'
  };

  function el(tag, cls, attrs) {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    Object.keys(attrs || {}).forEach((k) => node.setAttribute(k, attrs[k]));
    return node;
  }

  /** Parts → text and <strong>, never innerHTML. */
  function fillParts(node, parts) {
    node.textContent = '';
    (parts || []).forEach((part) => {
      if (part.b) {
        const strong = document.createElement('strong');
        strong.textContent = part.v;
        node.appendChild(strong);
      } else {
        node.appendChild(document.createTextNode(part.v));
      }
    });
  }

  function iconFor(stop) {
    return ICONS[stop.kind] || ICONS.gift;
  }

  function buildRail(m) {
    const wrap = el('div', 'gwp-rail');
    const head = el('div', 'gwp-rail__head');
    head.appendChild(el('span', 'gwp-rail__eyebrow'));
    head.appendChild(el('span', 'gwp-rail__count'));
    wrap.appendChild(head);
    wrap.appendChild(el('p', 'gwp-rail__message', { 'aria-live': 'polite' }));
    const bar = el('div', 'gwp-rail__bar', { role: 'img' });
    bar.appendChild(el('span', 'gwp-rail__track'));
    m.stops.forEach((s, i) => bar.appendChild(el('span', 'gwp-rail__seg', { 'data-i': String(i) })));
    m.stops.forEach((s, i) => {
      const stop = el('span', 'gwp-rail__stop', { 'data-i': String(i), 'data-kind': s.kind, 'aria-hidden': 'true' });
      stop.innerHTML = iconFor(s) + ICONS.tick;
      bar.appendChild(stop);
    });
    wrap.appendChild(bar);
    const labelRow = el('div', 'gwp-rail__labels', { 'aria-hidden': 'true' });
    m.stops.forEach((s, i) => {
      const label = el('span', 'gwp-rail__label', { 'data-i': String(i) });
      label.appendChild(el('span', 'gwp-rail__label-name'));
      label.appendChild(el('span', 'gwp-rail__label-req'));
      labelRow.appendChild(label);
    });
    wrap.appendChild(labelRow);
    wrap.appendChild(el('p', 'gwp-rail__banner', { hidden: '' }));
    return wrap;
  }

  /** Draw (or update in place, so the segments animate) one rail host. */
  function renderRail(host, m, state, banner) {
    if (!m.active) {
      host.hidden = true;
      return;
    }
    host.hidden = false;
    const shape = m.stops.map((s) => s.kind).join(',');
    let rail = host.querySelector('.gwp-rail');
    if (!rail || rail.dataset.shape !== shape) {
      host.textContent = '';
      rail = buildRail(m);
      rail.dataset.shape = shape;
      host.appendChild(rail);
    }
    const n = m.stops.length;
    rail.style.setProperty('--gwp-n', String(n));
    rail.dataset.complete = m.unlocked === m.total ? 'true' : 'false';

    const labels = state.labels || {};
    const progress = G.text(labels.progress || '[unlocked] of [total] unlocked', { unlocked: m.unlocked, total: m.total });
    const title = String(state.title || '').trim();
    const eyebrow = rail.querySelector('.gwp-rail__eyebrow');
    eyebrow.textContent = title;
    eyebrow.hidden = !title;
    rail.querySelector('.gwp-rail__count').textContent = progress;
    fillParts(rail.querySelector('.gwp-rail__message'), m.message);
    rail.querySelector('.gwp-rail__bar').setAttribute('aria-label',
      (title ? title + ': ' : '') + G.text(labels.railLabel || 'Rewards: [unlocked] of [total] unlocked', { unlocked: m.unlocked, total: m.total }));

    m.stops.forEach((s, i) => {
      const seg = rail.querySelector('.gwp-rail__seg[data-i="' + i + '"]');
      seg.style.setProperty('--gwp-i', String(i));
      seg.style.setProperty('--gwp-fill', String(Math.round(s.fill * 1000) / 1000));
      seg.dataset.state = s.state;
      const stop = rail.querySelector('.gwp-rail__stop[data-i="' + i + '"]');
      stop.style.setProperty('--gwp-i', String(i));
      stop.dataset.state = s.state;
      const label = rail.querySelector('.gwp-rail__label[data-i="' + i + '"]');
      label.style.setProperty('--gwp-i', String(i));
      label.dataset.state = s.state;
      label.dataset.last = i === n - 1 ? 'true' : 'false';
      label.querySelector('.gwp-rail__label-name').textContent = s.name;
      label.querySelector('.gwp-rail__label-req').textContent = s.req;
    });

    const bannerEl = rail.querySelector('.gwp-rail__banner');
    if (banner) {
      fillParts(bannerEl, banner.parts);
      bannerEl.dataset.key = banner.key;
      bannerEl.hidden = false;
    } else {
      bannerEl.hidden = true;
    }
  }

  function renderNotices(host, m) {
    host.textContent = '';
    const list = (m.active && m.notices) || [];
    host.hidden = list.length === 0;
    list.forEach((notice) => {
      const card = el('div', 'gwp-notice gwp-notice--' + notice.type, { 'data-gwp-notice': String(notice.n) });
      if (notice.type === 'hold') {
        const thumb = el('span', 'gwp-notice__image');
        if (notice.image) {
          const img = el('img', '', { src: notice.image, alt: '', width: '56', height: '56', loading: 'lazy' });
          thumb.appendChild(img);
        }
        card.appendChild(thumb);
        const body = el('div', 'gwp-notice__body');
        const heading = el('p', 'gwp-notice__title');
        fillParts(heading, notice.title);
        const text = el('p', 'gwp-notice__text');
        fillParts(text, notice.body);
        body.appendChild(heading);
        body.appendChild(text);
        card.appendChild(body);
      } else {
        const text = el('p', 'gwp-notice__text');
        fillParts(text, notice.text);
        card.appendChild(text);
        if (notice.type === 'declined') {
          const button = el('button', 'gwp-notice__action', { type: 'button', 'data-gwp-add-back': String(notice.n), 'aria-label': notice.actionLabel });
          button.textContent = notice.action;
          card.appendChild(button);
        }
      }
      host.appendChild(card);
    });
  }

  let pendingBanner = null;

  function render(state) {
    const m = G.model(state, { now: Date.now(), fmt: FMT, unconfirmed: readJson(UNCONFIRMED_KEY) || {} });

    // One-off banners: compare with what was unlocked at the last render of
    // this session (the first render of a session sets the baseline only). A
    // banner stays while only gift lines change, which covers the add that
    // follows an unlock, and goes with the customer's next change to the bag
    // or their next gift action (act() clears it).
    const previous = readJson(KEYS_KEY);
    let banner = m.active && previous ? G.banner(m, previous, state.copy || {}, state.labels || {}) : null;
    if (banner) {
      banner.signature = state.signature;
    } else if (pendingBanner && m.active && pendingBanner.signature === state.signature
      && m.keys.indexOf(pendingBanner.key) !== -1) {
      banner = pendingBanner;
    }
    pendingBanner = banner;
    if (m.active) writeJson(KEYS_KEY, m.keys);

    document.querySelectorAll('[data-gwp-rail]').forEach((host) => renderRail(host, m, state, banner));
    document.querySelectorAll('[data-gwp-notices]').forEach((host) => renderNotices(host, m));
    scheduleEnd(state);
    return m;
  }
  G.render = () => { const s = readState(); return s ? render(s) : null; };

  // --- Reconcile -------------------------------------------------------------

  let inFlight = false;
  let queued = false;
  let attempts = { signature: null, count: 0 };

  function reconcile() {
    if (inFlight) { queued = true; return Promise.resolve(); }
    const state = readState();
    if (!state) return Promise.resolve();
    render(state);

    const unconfirmed = readJson(UNCONFIRMED_KEY) || {};
    const payload = G.plan(state, { now: Date.now(), unconfirmed });
    if (payload && Object.keys(payload.unconfirm).length) {
      Object.keys(payload.unconfirm).forEach((n) => {
        unconfirmed[n] = payload.unconfirm[n];
        // eslint-disable-next-line no-console
        console.warn('[Gifts] gift ' + n + ' was not made free by its discount; it has been taken out of the bag');
      });
      writeJson(UNCONFIRMED_KEY, unconfirmed);
    }
    if (!payload || !hasWork(payload)) {
      attempts = { signature: state.signature, count: 0 };
      return Promise.resolve();
    }
    if (attempts.signature === state.signature && attempts.count >= MAX_ATTEMPTS) return Promise.resolve();
    attempts = { signature: state.signature, count: attempts.signature === state.signature ? attempts.count + 1 : 1 };

    inFlight = true;
    return execute(payload, state)
      .then((added) => { added.forEach((n) => track('gwp_gift_added', { tier: n })); })
      .catch((error) => {
        // Never let this break the bag.
        // eslint-disable-next-line no-console
        console.warn('[Gifts] reconcile failed', error);
      })
      .finally(() => {
        inFlight = false;
        if (queued) { queued = false; schedule(); }
      });
  }
  G.reconcile = reconcile;

  let scheduled = null;
  function schedule() {
    clearTimeout(scheduled);
    scheduled = setTimeout(() => { scheduled = null; reconcile(); }, 150);
  }

  // A tier or the offer ending while the page is open: redraw at once, then
  // ask for a fresh render so the gift leaves and the shipping bar returns.
  let endTimer = null;
  let endAt = 0;
  function scheduleEnd(state) {
    const now = Date.now();
    const ends = (state.tiers || []).concat(state.offer ? [state.offer] : [])
      .map((x) => (Number(x.end) || 0) * 1000)
      .filter((ms) => ms > now);
    const next = ends.length ? Math.min.apply(null, ends) : 0;
    if (next === endAt) return;
    clearTimeout(endTimer);
    endAt = next;
    if (next && next - now < 2147483647) {
      endTimer = setTimeout(() => { endAt = 0; reconcile(); refreshCart(); }, next - now + 1000);
    }
  }

  // --- Customer actions --------------------------------------------------------

  function act(target, payload, eventName, params, state) {
    if (!payload || inFlight) return;
    pendingBanner = null;
    const line = target.closest('.cart-item--gift');
    if (line) line.classList.add('gwp-busy');
    target.setAttribute('aria-busy', 'true');
    inFlight = true;
    execute(payload, state)
      .then(() => track(eventName, params))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('[Gifts] action failed', error);
        refreshCart();
      })
      .finally(() => {
        inFlight = false;
        if (queued) { queued = false; schedule(); }
      });
  }

  document.addEventListener('click', (evt) => {
    const choose = evt.target.closest('[data-gwp-choose]');
    const decline = evt.target.closest('[data-gwp-decline]');
    const addBack = evt.target.closest('[data-gwp-add-back]');
    if (!choose && !decline && !addBack) return;
    evt.preventDefault();
    const state = readState();
    if (!state) return;
    if (choose) {
      if (choose.getAttribute('aria-pressed') === 'true') return;
      const n = choose.getAttribute('data-gwp-choose');
      act(choose, G.planSwap(state, n, choose.getAttribute('data-gwp-product')), 'gwp_colour_changed', { tier: n }, state);
    } else if (decline) {
      const n = decline.getAttribute('data-gwp-decline');
      act(decline, G.planDecline(state, n), 'gwp_gift_removed', { tier: n }, state);
    } else {
      const n = addBack.getAttribute('data-gwp-add-back');
      act(addBack, G.planAddBack(state, n, { now: Date.now(), unconfirmed: readJson(UNCONFIRMED_KEY) || {} }), 'gwp_gift_restored', { tier: n }, state);
    }
  });

  // --- Start -------------------------------------------------------------------

  document.addEventListener('on:cart:after-merge', schedule);

  // First load (001 §3.11): no merge has run, and a prefetched page can carry
  // an old drawer. Act only on a render that matches the live cart; otherwise
  // ask for a fresh one and act on its merge.
  function start() {
    const state = readState();
    if (!state) return;
    render(state);
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (!cart) return;
        if (G.cartSignature(cart) === state.signature) reconcile();
        else refreshCart();
      })
      .catch(() => { /* the next cart change reconciles */ });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
